import { AuditAction, Platform } from '@/constants';
import { dateFormatInUserTimeZone } from '@/helpers';
import { AbstractModel } from '@/models/abstractModel';
import { auditActionDictionary } from '@/stores/simpleDictionary/auditAction';
import type { IAuditDataChange, IAuditExpandedChange, IAuditRow } from '@/types/audit';

const AUDIT_DATE_FORMAT = 'dd MMM yyyy HH:mm:ss';

function isEmptyAuditValue(value: string | number | null | undefined): boolean {
    return value == null || value === '' || value === 0 || value === '0';
}

function getChangeEntry(change: IAuditDataChange | undefined): { field: string; value: string | number | null } | null {
    if (!change) return null;
    const entry = Object.entries(change)[0];
    if (!entry) return null;
    return { field: entry[0], value: entry[1] };
}

/** Only object/array JSON strings — not `"1"`, `"true"`, plain text. */
function tryParseJsonObject(value: string | number | null | undefined): unknown | undefined {
    if (typeof value !== 'string') return undefined;
    const trimmed = value.trim();
    if (!(trimmed.startsWith('{') || trimmed.startsWith('['))) return undefined;
    try {
        const parsed = JSON.parse(trimmed);
        if (parsed !== null && typeof parsed === 'object') return parsed;
    } catch {
        // not valid JSON
    }
    return undefined;
}

function formatAuditValue(value: string | number | null | undefined): { text: string; isJson: boolean } {
    const parsed = tryParseJsonObject(value);
    if (parsed !== undefined) {
        return { text: JSON.stringify(parsed, null, 2), isJson: true };
    }
    return { text: isEmptyAuditValue(value) ? 'Empty' : String(value), isJson: false };
}

/** One-line cell/tooltip: JSON → `{...}` / `[...]`, otherwise same text as expand. */
function collapsedAuditValueText(text: string, isJson: boolean): string {
    if (!isJson) return text;
    return text.trimStart().startsWith('[') ? '[...]' : '{...}';
}

function formatCollapsedChange(change: IAuditExpandedChange): string {
    const newText = collapsedAuditValueText(change.newText, change.newIsJson);
    const oldPart = change.oldText ? ` (${collapsedAuditValueText(change.oldText, change.oldIsJson)})` : '';
    return `${change.field} = ${newText}${oldPart}`;
}

export class AuditItem extends AbstractModel {
    public actionId: number;
    public actionName: string;
    public auditId: number;
    public author: string;
    public countChanges: number;
    public createdAt: string;
    public editedRecordName: string;
    public fieldName: string;
    public isTradeballUser: boolean;
    public newDataFormatted: IAuditDataChange[];
    public newValue: string | null;
    public oldDataFormatted: IAuditDataChange[];
    public oldValue: string | null;
    public sourceId: number;
    public tableId: number;
    public tableName: string;
    public type: string;
    public userId: number;
    public userPlatformId: number;

    constructor(data: IAuditRow) {
        super();
        this.type = data.type;
        this.tableId = data.table_id;
        this.tableName = data.table_name;
        this.createdAt = data.created_at;
        this.auditId = data.audit_id;
        this.actionId = data.action_id;
        this.actionName = data.action_name;
        this.sourceId = data.source_id;
        this.author = data.author;
        this.userId = data.user_id;
        this.userPlatformId = data.user_platform_id;
        this.isTradeballUser = data.user_platform_id === Platform.Tradeball;
        this.editedRecordName = data.edited_record_name;
        this.fieldName = data.field_name;
        this.oldValue = data.old_value;
        this.newValue = data.new_value;
        this.oldDataFormatted = data.old_data_formatted ?? [];
        this.newDataFormatted = data.new_data_formatted ?? [];
        this.countChanges = data.count_changes;
    }

    static actionClassFor(actionId: number | null | undefined): string {
        switch (actionId) {
            case AuditAction.Add:
                return 'text-tbGreen';
            case AuditAction.Update:
                return 'text-tbBlue';
            case AuditAction.Delete:
                return 'text-tbRed';
            default:
                return '';
        }
    }

    static collapsedValueText(text: string, isJson: boolean): string {
        return collapsedAuditValueText(text, isJson);
    }

    get actionClass(): string {
        return AuditItem.actionClassFor(this.actionId);
    }

    get actionLabel(): string {
        return auditActionDictionary.nameById(this.actionId) || this.actionName || String(this.actionId);
    }

    /** Plain one-line summary for tooltip / fallback (same rules as the Edited fields cell). */
    get collapsedEditedFieldsPlain(): string {
        return this.expandedChanges.map(formatCollapsedChange).join(', ');
    }

    get expandedChanges(): IAuditExpandedChange[] {
        return this.newDataFormatted
            .map((change, index) => {
                const newEntry = getChangeEntry(change);
                if (!newEntry) return null;

                const oldByField = this.oldDataFormatted.find((row) => newEntry.field in row);
                const oldByIndex = getChangeEntry(this.oldDataFormatted[index]);
                const oldValue = oldByField?.[newEntry.field] ?? (oldByIndex?.field === newEntry.field ? oldByIndex.value : undefined);

                const formattedNew = formatAuditValue(newEntry.value);
                const showOld = !isEmptyAuditValue(oldValue);
                const formattedOld = showOld ? formatAuditValue(oldValue) : null;

                return {
                    field: newEntry.field,
                    newText: formattedNew.text,
                    newIsJson: formattedNew.isJson,
                    oldText: formattedOld?.text ?? null,
                    oldIsJson: formattedOld?.isJson ?? false,
                };
            })
            .filter((row): row is IAuditExpandedChange => row != null);
    }

    get formatCreatedAt(): string {
        return dateFormatInUserTimeZone(this.createdAt, AUDIT_DATE_FORMAT);
    }

    get id(): number {
        return this.auditId;
    }
}
